import React from 'react';
import { Search, X, SlidersHorizontal } from 'lucide-react';

const RISK_TABS = [
  { key: 'all', label: 'All', active: 'bg-white text-slate-800 shadow-sm' },
  { key: 'high', label: 'High', active: 'bg-white text-red-700 shadow-sm' },
  { key: 'medium', label: 'Medium', active: 'bg-white text-amber-700 shadow-sm' },
  { key: 'low', label: 'Low', active: 'bg-white text-emerald-700 shadow-sm' },
];

const CATEGORIES = [
  { value: '', label: 'All categories' },
  { value: 'legal', label: 'Legal' },
  { value: 'regulatory', label: 'Regulatory' },
  { value: 'hr', label: 'HR' },
  { value: 'criminal', label: 'Criminal' },
  { value: 'financial', label: 'Financial' },
  { value: 'compliance', label: 'Compliance' },
];

const SORT_OPTIONS = [
  { value: 'newest', label: 'Newest first' },
  { value: 'oldest', label: 'Oldest first' },
];

function severityToRiskTab(severityFilter) {
  if (!severityFilter) return 'all';
  if (['critical', 'high'].includes(severityFilter)) return 'high';
  if (severityFilter === 'medium') return 'medium';
  if (severityFilter === 'low') return 'low';
  return 'all';
}

const selectClass =
  'h-8 text-[13px] font-medium text-[#0d1524] bg-white border border-[#cbd3df] rounded-md px-2.5 pr-7 focus:outline-none focus:border-[#1c4ed8] cursor-pointer hover:bg-[#f6f7fa] transition';

function AlertFiltersBar({
  title = 'Latest Alerts',
  severityFilter,
  setSeverityFilter,
  categoryFilter,
  setCategoryFilter,
  sortOrder,
  setSortOrder,
  resultCount,
  onViewAll,
  compact = false,
  showCategory = true,
  showSort = true,
  leadingExtraFilters,
  extraFilters,
  searchValue,
  setSearchValue,
  searchPlaceholder = 'Search company, person, or keyword…',
}) {
  const activeRisk = severityToRiskTab(severityFilter);

  const setRisk = (key) => {
    if (key === 'all') setSeverityFilter('');
    else setSeverityFilter(key);
  };

  return (
    <div className={`flex flex-col ${compact ? 'gap-4' : 'gap-4 mb-4'}`}>
      {/* Title row (optional) */}
      {title && (
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2.5 min-w-0">
          <h3 className="text-lg font-bold text-slate-900 tracking-tight">{title}</h3>
          {resultCount != null && (
            <span className="text-xs font-semibold text-slate-500 bg-slate-100 border border-slate-200/80 px-2.5 py-1 rounded-full tabular-nums">
              {resultCount} {resultCount === 1 ? 'result' : 'results'}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {extraFilters}
          {onViewAll && (
            <button
              type="button"
              onClick={onViewAll}
              className="h-10 px-3.5 text-sm font-semibold text-blue-600 hover:bg-blue-50 rounded-xl transition whitespace-nowrap"
            >
              View all
            </button>
          )}
        </div>
      </div>
      )}

      {/* Filters row */}
      <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
        <div className="relative z-10 flex flex-wrap items-center gap-2.5 min-w-0">
          <div className="flex items-center gap-1.5 text-slate-400 mr-0.5" title="Filters">
            <SlidersHorizontal size={15} />
          </div>

          {/* Risk severity */}
          <div className="flex items-center gap-0.5 bg-[#f6f7fa] border border-[#e1e5ec] rounded-md p-0.5">
            {RISK_TABS.map(tab => (
              <button
                key={tab.key}
                type="button"
                onClick={() => setRisk(tab.key)}
                className={`px-2.5 py-1 text-[13px] font-medium rounded transition whitespace-nowrap ${
                  activeRisk === tab.key
                    ? tab.active
                    : 'text-slate-500 hover:text-slate-800'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {leadingExtraFilters}

          {showCategory && setCategoryFilter && (
            <select
              value={categoryFilter}
              onChange={e => setCategoryFilter(e.target.value)}
              className={selectClass}
              aria-label="Category"
            >
              {CATEGORIES.map(c => (
                <option key={c.value || 'all'} value={c.value}>{c.label}</option>
              ))}
            </select>
          )}

          {showSort && setSortOrder && (
            <select
              value={sortOrder}
              onChange={e => setSortOrder(e.target.value)}
              className={selectClass}
              aria-label="Sort"
            >
              {SORT_OPTIONS.map(o => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          )}

          {!title && extraFilters}
        </div>

        {setSearchValue && (
          <div className="relative w-full xl:w-72 flex-shrink-0">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
            <input
              type="text"
              value={searchValue}
              onChange={e => setSearchValue(e.target.value)}
              placeholder={searchPlaceholder}
              className="h-8 w-full text-[13px] text-[#0d1524] bg-white border border-[#cbd3df] rounded-md pl-9 pr-9 focus:outline-none focus:border-[#1c4ed8] placeholder:text-[#76829a] transition"
            />
            {searchValue && (
              <button
                type="button"
                onClick={() => setSearchValue('')}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 p-1 text-slate-300 hover:text-slate-500 rounded-md"
              >
                <X size={14} />
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default AlertFiltersBar;
export { RISK_TABS, severityToRiskTab };
